import { __read, __spread } from "tslib";
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/**
 * Transformers change and combine streams, similar to operators.
 * urx comes with two combinators - [[combineLatest]] and [[merge]], and one convenience filter - [[duc]].
 *
 * @packageDocumentation
 */
import { publish, reset, subscribe } from './actions';
import { RESET, SUBSCRIBE } from './constants';
import { defaultComparator, distinctUntilChanged, pipe } from './pipe';
import { stream } from './streams';
import { joinProc } from './utils';
/**
 * Merges one or more emitters from the same type into a new Emitter which emits values from any of the source emitters.
 * ```ts
 * const foo = stream<number>()
 * const bar = stream<number>()
 *
 * subscribe(merge(foo, bar), (value) => console.log(value)) // 42, 43
 *
 * publish(foo, 42)
 * publish(bar, 43)
 * ```
 */
export function merge() {
    var sources = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        sources[_i] = arguments[_i];
    }
    return function (action, subscription) {
        switch (action) {
            case SUBSCRIBE:
                return joinProc.apply(void 0, __spread(sources.map(function (source) { return subscribe(source, subscription); })));
            case RESET:
                // do nothing, we are stateless
                return;
            default:
                throw new Error("unrecognized action " + action);
        }
    };
}
/**
 * A convenience wrapper that emits only the distinct values from the passed Emitter. Wraps [[pipe]] and [[distinctUntilChanged]].
 *
 * ```ts
 * const foo = stream<number>()
 *
 * // this line...
 * const a = duc(foo)
 *
 * // is equivalent to this
 * const b = pipe(distinctUntilChanged(foo))
 * ```
 *
 * @param source The source emitter.
 * @param comparator optional custom comparison function for the two values.
 *
 * @typeParam T the type of the value emitted by the source.
 *
 * @returns the resulting emitter.
 */
export function duc(source, comparator) {
    if (comparator === void 0) { comparator = defaultComparator; }
    return pipe(source, distinctUntilChanged(comparator));
}
export function combineLatest() {
    var emitters = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        emitters[_i] = arguments[_i];
    }
    var innerSubject = stream();
    var values = new Array(emitters.length);
    var called = 0;
    var allCalled = Math.pow(2, emitters.length) - 1;
    emitters.forEach(function (source, index) {
        var bit = Math.pow(2, index);
        subscribe(source, function (value) {
            values[index] = value;
            called = called | bit;
            if (called === allCalled) {
                publish(innerSubject, values);
            }
        });
    });
    return function (action, subscription) {
        switch (action) {
            case SUBSCRIBE:
                if (called === allCalled) {
                    subscription(values);
                }
                return subscribe(innerSubject, subscription);
            case RESET:
                return reset(innerSubject);
            default:
                throw new Error("unrecognized action " + action);
        }
    };
}
